import { makeStyles, Typography } from '@material-ui/core'
import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormContext, useWatch } from 'react-hook-form'
import { FEE_CALC_TYPES, TEXT_VARIANTS } from '../../../../constants'
import ListCard from '../../../molecules/ListCard'
import Select from '../../../molecules/Select'
import { capitalizeFirstLetter, feeLimitsDetailFormatter, numberToBPS, numberToUSD, pluralize } from '../../../../utils'
import Text from '../../../atoms/Text'
import { useFeeSchedule } from '../../../../api/billing'
import { mapFeeScheduleOptions } from '../AccountDetails/helpers'
import Skeleton from '../../../atoms/Skeleton'
import FeeOverridesList from '../FeeOverrides'
import { FEE_LEVEL_OVERRIDE_OPTIONS, FEE_OVERRIDE_LEVEL_VALUES, findOverrideOption } from '../FeeOverrides/helpers'
import RemoveButton from '../FeeOverrides/RemoveButton'
import EmptySection from '../../../atoms/EmptySection'
import MenuItem from './MenuItem'
import { CLIENT_BILLING_FORM_NAMES } from './helpers'

const useStyles = makeStyles((theme) => ({
  container: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column'
  },
  titleContainer: {
    textAlign: 'left',
    marginBottom: theme.spacing(2)
  },
  spaceDivider: {
    minHeight: '1rem',
    width: '100%'
  },
  title: {
    textAlign: 'left',
    fontSize: '1.5rem'
  },
  detailsContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  multipleDetailsContent: {
    flexDirection: 'column'
  },
  itemContent: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between'
  },
  itemRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  itemAction: {
    cursor: 'pointer',
    marginLeft: '1rem'
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    cursor: 'pointer'
  },
  feeDetail: {
    display: 'flex',
    fontWeight: 400,
    flexDirection: 'row'
  }
}))

const ClientBillingDetails = ({
  editMode,
  disabled
}) => {
  const classes = useStyles()
  const {
    setValue,
    formState: { errors, defaultValues }
  } = useFormContext()

  const feeScheduleTypeValue = useWatch({ name: 'feeScheduleType' })

  const { onChangeFeeScheduleType } = useMemo(() => {
    return Object.values(CLIENT_BILLING_FORM_NAMES).reduce(
      (acc, name) => ({
        ...acc,
        [`onChange${capitalizeFirstLetter(name)}`]: (value) => {
          setValue(name, value, { shouldDirty: true })
        }
      }),
      {}
    )
  }, [setValue])

  const {
    data: feeScheduleOptions = [],
    isLoading: isLoadingFeeScheduleOptions
  } = useFeeSchedule(mapFeeScheduleOptions)

  const selectedFeeScheduleDetails = useMemo(() => {
    const feeScheduleOverride = feeScheduleOptions.find(
      ({ value }) => value === feeScheduleTypeValue
    )
    if (!feeScheduleOverride) return null

    const feeSchedule = feeScheduleOverride.payload

    return (
      <div className={classes.container}>
        {feeSchedule.details.map((feeScheduleDetails) => {
          return feeScheduleDetails.map((detail, index) => {
            const showTier = detail.calcTypeId === FEE_CALC_TYPES.TIERED_SCHEDULE || detail.calcTypeId === FEE_CALC_TYPES.FLOATING_RATE
            const isDollarRate = detail.calcTypeId === FEE_CALC_TYPES.FLAT_DOLLAR
            const rateNumber = isDollarRate ? detail.rate : numberToBPS(detail.rate)
            const denomination = detail.denomination.toUpperCase()
            const feeDescription = isDollarRate
              ? `${numberToUSD(rateNumber)} ${pluralize(rateNumber, denomination, 'S')}`
              : `${rateNumber} ${denomination}`

            return (
              <div
                key={`fee-${feeSchedule.id}-${index}`}
                className={classes.feeDetail}
              >
                {
                  showTier &&
                    <Text
                      text={feeLimitsDetailFormatter(
                        detail.limits.lower,
                        detail.limits.upper
                      )}
                      customFontWeight='bold'
                    />
                }
                &nbsp;
                <Text
                  text={feeDescription}
                />
              </div>
            )
          })
        })}
      </div>
    )
  }, [
    classes.container,
    classes.feeDetail,
    feeScheduleOptions,
    feeScheduleTypeValue
  ])

  const feeScheduleOptionsRenderer = useCallback(
    ({ payload: feeSchedule }, onChange, checkedIcon) => {
      const { id, name, description } = feeSchedule
      const item = { id, name, description }
      return <MenuItem item={item} checked={checkedIcon} onChange={onChange} />
    },
    []
  )

  const feeScheduleSelectProps = useMemo(
    () => ({
      options: feeScheduleOptions,
      isLoading: isLoadingFeeScheduleOptions,
      optionsRenderer: feeScheduleOptionsRenderer
    }),
    [
      feeScheduleOptions,
      feeScheduleOptionsRenderer,
      isLoadingFeeScheduleOptions
    ]
  )

  const levelOverrideOptions = useMemo(() => {
    const assetClassOption = findOverrideOption(FEE_OVERRIDE_LEVEL_VALUES.class)
    const positionOption = findOverrideOption(FEE_OVERRIDE_LEVEL_VALUES.position)
    const accountOption = findOverrideOption(FEE_OVERRIDE_LEVEL_VALUES.account)
    return [
      ...FEE_LEVEL_OVERRIDE_OPTIONS.filter(
        ({ value }) =>
          value !== accountOption.value && value !== positionOption.value && value !== assetClassOption.value
      ),
      {
        ...assetClassOption,
        rules: {
          ...assetClassOption.rules,
          searchQueryOptions: {
            defaultClassificationTagIds: defaultValues?.feeOverride?.map(feeOverride => feeOverride.feeOverrideLevel),
            query: { filters: { classificationTagTypeId: 51 } }
          }
        }
      },
      {
        ...accountOption,
        rules: {
          ...accountOption.rules,
          searchQueryOptions: {
            assignedToClientIds: [defaultValues.clientId],
            defaultAccountIds: defaultValues?.feeOverride?.map(feeOverride => feeOverride.feeOverrideLevel),
            query: {
              filters: { assignedToClientIds: [defaultValues.clientId] }
            }
          }
        }
      },
      {
        ...positionOption,
        rules: {
          ...positionOption.rules,
          searchQueryOptions: {
            queryOptions: {
              levelType: 'client',
              levelId: defaultValues.clientId
            },
            defaultPositionId: defaultValues?.feeOverride?.map(feeOverride => feeOverride.feeOverrideLevel)
          }
        }
      }
    ]
  }, [defaultValues])

  const renderFeeSchedule = useMemo(() => {
    return (
      <div className={classes.itemRow}>
        <Select
          disabled={disabled}
          placeholder={editMode ? 'Select Fee Schedule' : ''}
          readOnly={!editMode}
          value={feeScheduleTypeValue}
          onChange={onChangeFeeScheduleType}
          options={feeScheduleOptions}
          optionsRenderer={feeScheduleOptionsRenderer}
          error={errors?.feeScheduleType?.message}
        />
        {Boolean(feeScheduleTypeValue) && editMode && (
          <RemoveButton onClick={() => onChangeFeeScheduleType('')} />
        )}
        {selectedFeeScheduleDetails}
      </div>
    )
  }, [
    classes.itemRow,
    disabled,
    editMode,
    errors?.feeScheduleType?.message,
    feeScheduleOptions,
    feeScheduleOptionsRenderer,
    feeScheduleTypeValue,
    onChangeFeeScheduleType,
    selectedFeeScheduleDetails
  ])

  const renderEmptyFeeSchedule = useMemo(() => {
    return (
      <EmptySection
        title='No Fee Schedule'
        description=''
        styles={{ cursor: 'pointer' }}
      />
    )
  }, [])

  const renderFeeScheduleSection = useMemo(() => {
    if (isLoadingFeeScheduleOptions) {
      return <Skeleton height='2.5rem' width='100%' />
    }

    if (feeScheduleTypeValue || editMode) {
      return renderFeeSchedule
    }

    return renderEmptyFeeSchedule
  }, [
    editMode,
    feeScheduleTypeValue,
    isLoadingFeeScheduleOptions,
    renderEmptyFeeSchedule, renderFeeSchedule
  ])

  return (
    <div className={classes.container}>
      <div className={classes.titleContainer}>
        <Typography variant={TEXT_VARIANTS.h3} className={classes.title}>
          Billing Details
        </Typography>
      </div>
      <ListCard
        title='Fee Schedule'
        disableContainerHighlight={!editMode || disabled}
      >
        <div className={classes.detailsContent}>
          {renderFeeScheduleSection}
        </div>
      </ListCard>
      <div className={classes.spaceDivider} />
      <FeeOverridesList
        editMode={editMode}
        levelOverrideOptions={levelOverrideOptions}
        feeScheduleSelectProps={feeScheduleSelectProps}
      />
      <div className={classes.spaceDivider} />
    </div>
  )
}

ClientBillingDetails.propTypes = {
  editMode: PropTypes.bool,
  disabled: PropTypes.bool
}

ClientBillingDetails.defaultProps = {
  editMode: false,
  disabled: false
}

export default ClientBillingDetails
